import React, { useCallback, useEffect, useMemo, useState } from "react";
import { styled, SxProps } from "@mui/material/styles";
import FormHelperText from "@mui/material/FormHelperText";
import Box, { BoxProps } from "@mui/material/Box";
import type { ErrorCode, FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";

import { EmtIcon } from "../EmtIcon";
import { EmtTypography } from "../EmtTypography";
import { AddPictureIcon } from "../../Icons/AddPictureIcon";
import { EmtTheme } from "../../types";

/* ========================
   Types
======================== */

export type EmtFileDropZoneProps = {
  /**
   * Variant of the dropZone box
   * @default default
   */
  variant?: "default" | "small";
  /**
   * File types accepted by the input
   * @default image/*
   * @example { 'image/png': ['.png'] }
   */
  accept?: Record<string, string[]>;
  /**
   * Extra extension allow-list (case-insensitive)
   */
  acceptedExtensions?: string | string[];
  /**
   * Max files selectable
   * @default 1
   */
  maxFiles?: number;
  /**
   * Left & right decorative clouds (default variant)
   */
  cloud1?: string;
  cloud2?: string;
  /**
   * Illustration url (shown when no file selected)
   */
  illustration?: string;
  /**
   * i18n for errors
   */
  errorTranslate: {
    [ErrorCode.FileInvalidType]: string;
    "file-image-invalid-type": string;
    [ErrorCode.FileTooLarge]: string;
    [ErrorCode.FileTooSmall]: string;
    [ErrorCode.TooManyFiles]: string;
    [str: string]: string;
  };
  /**
   * Preview (dataURL/base64) provided by parent (controlled)
   */
  preview?: string | ArrayBuffer | null;
  /**
   * Callback on selection (dataURL/base64) or null on delete
   */
  onDrop?: (file: string | ArrayBuffer | null) => void;
  /**
   * Helper/error text
   */
  helperText?: string;
  /**
   * Error state for helper text color
   */
  error?: boolean;
  /**
   * Style override for the box
   */
  overrideDropZoneBox?: SxProps;
  /**
   * Show / hide delete button
   * @default true
   */
  activeDeleteButton?: boolean;
  /**
   * Show a "re-crop" (clockwise) affordance; parent can intercept with overrideOpenImageDialog
   * @default true
   */
  activeRotation?: boolean;
  /**
   * Used ONLY by the re-crop (clockwise) icon. If provided, clicking that icon calls this,
   * otherwise it falls back to opening the file chooser.
   */
  overrideOpenImageDialog?: (open: () => void) => void;
  /**
   * Allow selecting multiple files (component still uses the first one)
   * @default false
   */
  multipleFiles: boolean;
  nonce?: string;
  /**
   * File rejections callback
   */
  onFileReject?: (fileRejections: FileRejection[]) => void;

  /** If true, never show internal auto-preview; use only the controlled `preview` prop */
  controlledPreview?: boolean;
};

type DropZoneBoxWrapperProps = BoxProps & {
  cloud1?: string;
  cloud2?: string;
  variant?: "default" | "small";
};

const DropZoneBoxWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "cloud1" && prop !== "cloud2",
})((props: DropZoneBoxWrapperProps) => {
  const { cloud1, cloud2, variant } = props;
  return variant === "default"
    ? {
        background: `url(${cloud1}) no-repeat left 10%, url(${cloud2}) no-repeat right 30%`,
      }
    : {};
});

type DropZoneBoxProps = BoxProps & {
  isFocused: boolean;
  isDragReject: boolean;
  isDragAccept: boolean;
  file?: string; // we pass a string URL/base64 only
  theme?: EmtTheme;
};

const DefaultDropZoneBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isFocused" &&
    prop !== "isDragReject" &&
    prop !== "isDragAccept" &&
    prop !== "file",
})((props: DropZoneBoxProps) => {
  const { theme, isFocused, isDragReject, isDragAccept, file } = props;
  return {
    position: "relative",
    borderWidth: 2,
    borderRadius: "18px",
    borderColor: file ? "transparent" : theme?.palette.primary.main,
    borderStyle: "dashed",
    transition: "border .24s ease-in-out",
    outline: "none",
    padding: "15px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    width: "120px",
    height: "120px",
    ...(file
      ? {
          background: `url('${file}') center`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }
      : {}),
    ...(isFocused
      ? { borderColor: file ? "transparent" : theme?.palette.primary.light }
      : {}),
    ...(isDragAccept
      ? { borderColor: file ? "transparent" : theme?.palette.success.main }
      : {}),
    ...(isDragReject
      ? { borderColor: file ? "transparent" : theme?.palette.error.dark }
      : {}),
  };
});

const SmallDropZoneBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isFocused" &&
    prop !== "isDragReject" &&
    prop !== "isDragAccept" &&
    prop !== "file",
})((props: DropZoneBoxProps) => {
  const { theme, isFocused, isDragReject, isDragAccept, file } = props;
  return {
    position: "relative",
    borderRadius: "16px",
    width: "65px",
    height: "65px",
    transition: "border .24s ease-in-out",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: `2px dashed ${
      file
        ? "transparent"
        : theme?.palette.greyGradient
        ? theme?.palette.greyGradient[200]
        : "#BBBABF"
    }`,
    borderWidth: 2,
    cursor: "pointer",
    ...(file
      ? {
          background: `url('${file}') center`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }
      : {}),
    ...(isFocused
      ? { borderColor: file ? "transparent" : theme?.palette.primary.light }
      : {}),
    ...(isDragAccept
      ? { borderColor: file ? "transparent" : theme?.palette.success.main }
      : {}),
    ...(isDragReject
      ? { borderColor: file ? "transparent" : theme?.palette.error.dark }
      : {}),
  };
});

const StyledDefaultEmtIcon = styled(EmtIcon)(() => ({
  background: "white",
  borderRadius: "100%",
  padding: "10px",
  position: "absolute",
  bottom: "-19px",
  fontSize: "16px",
  boxShadow: "0px 5px 5px rgb(0 0 0 / 8%), 0px 4px 16px rgb(0 0 0 / 8%)",
}));

const StyledSmallEmtIcon = styled(EmtIcon)((props) => {
  const { theme } = props as any;
  return {
    background: theme.palette.primary.main,
    border: `3px solid ${theme.palette.common.white}`,
    borderRadius: "100%",
    padding: "6px",
    position: "absolute",
    bottom: "-19px",
    fontSize: "7px",
    color: "white",
    cursor: "pointer",
  };
});

const StyledFormHelperText = styled(FormHelperText)(
  ({ theme }: { theme?: EmtTheme }) => ({
    fontFamily: "Nunito Sans",
    display: "block",
    textAlign: "center",
    marginTop: "15px",
    "&.error": { color: theme?.palette.error[450] },
  })
);

type FileType = Omit<File, "preview"> & { preview: string };

/* ========================
   Component
======================== */

export function EmtFileDropZone(props: EmtFileDropZoneProps) {
  const {
    variant = "default",
    accept = {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/webp": [".webp"],
      "image/gif": [".gif"],
      "image/bmp": [".bmp"],
      "image/tiff": [".tiff"],
      "image/heic": [".heic"],
      "image/heif": [".heif"],
    },
    acceptedExtensions = [
      "png",
      "jpg",
      "jpeg",
      "webp",
      "gif",
      "bmp",
      "tiff",
      "heic",
      "heif",
    ],
    errorTranslate,
    cloud1,
    cloud2,
    illustration,
    maxFiles = 1,
    preview,
    onDrop = () => null,
    helperText,
    error,
    overrideDropZoneBox = {},
    activeDeleteButton = true,
    overrideOpenImageDialog,
    multipleFiles = false,
    onFileReject,
    controlledPreview = false,
  } = props;

  const [files, setFiles] = useState<Array<FileType>>([]);
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([]);

  const firstFile = files[0];

  // Internal preview state (only used when NOT controlled)
  const [filePreview, setFilePreview] = useState<string | ArrayBuffer>(
    controlledPreview ? "" : preview || firstFile?.preview || ""
  );

  // Ensure background uses ONLY external preview in controlled mode
  const fileBg = controlledPreview
    ? typeof preview === "string"
      ? preview
      : undefined
    : typeof filePreview === "string"
    ? filePreview
    : undefined;

  const extensionsList = useMemo<string[]>(
    () =>
      Array.isArray(acceptedExtensions)
        ? acceptedExtensions
        : acceptedExtensions
        ? acceptedExtensions
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    [acceptedExtensions]
  );

  const getFileExtension = (fileName: string) =>
    fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setFiles([]);
    setFileRejections([]);
    setFilePreview("");
    onDrop(null);
  };

  const handleOnDrop = useCallback(
    (acceptedFiles: Array<File>, rejectedFiles: FileRejection[]) => {
      // Extra extension filter in addition to react-dropzone "accept"
      const filtered = acceptedFiles.filter((file) =>
        extensionsList.includes(getFileExtension(file.name))
      );

      const extensionRejectedFiles = acceptedFiles
        .filter((f) => !filtered.includes(f))
        .map((file) => ({
          file,
          errors: [
            { code: "file-image-invalid-type" as ErrorCode, message: "" },
          ],
        }));

      const allRejectedFiles = [...rejectedFiles, ...extensionRejectedFiles];
      setFileRejections(allRejectedFiles);
      if (onFileReject) onFileReject(allRejectedFiles);

      const newFiles = filtered.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      ) as FileType[];

      setFiles(newFiles);

      if (newFiles.length > 0) {
        const reader = new FileReader();
        reader.readAsDataURL(newFiles[0]);
        reader.onload = function () {
          const dataUrl = reader.result as string;

          // ⛔️ Do NOT show internal preview in controlled mode
          if (!controlledPreview) {
            setFilePreview(dataUrl);
          }

          // ✅ Still tell the parent so it can open the cropper
          onDrop(dataUrl);
        };
      }
    },
    [extensionsList, onFileReject, onDrop, controlledPreview]
  );

  useEffect(() => {
    if (!controlledPreview) {
      setFilePreview(preview || "");
      if (!preview) setFiles([]);
    }
  }, [preview, controlledPreview]);

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    accept,
    onDrop: handleOnDrop,
    maxFiles,
    noClick: true,
    multiple: multipleFiles,
  });

  // Compose root props so clicking ALWAYS opens the picker
  const rootClickProps = getRootProps({
    onClick: (e: any) => {
      e.preventDefault();
      open(); // always open the file chooser on click
    },
  });

  // Keep external preview in sync (controlled mode)
  useEffect(() => {
    setFilePreview(preview || "");
    if (!preview) setFiles([]);
  }, [preview]);

  const hasFile = !!fileBg;

  return (
    <DropZoneBoxWrapper cloud1={cloud1} cloud2={cloud2} variant={variant}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        {variant === "default" ? (
          <DefaultDropZoneBox
            file={fileBg}
            isFocused={isFocused}
            isDragAccept={isDragAccept}
            isDragReject={isDragReject}
            {...rootClickProps}
            {...(overrideOpenImageDialog && {
              onClick: () => overrideOpenImageDialog(open),
            })}
            sx={overrideDropZoneBox}
          >
            <input
              {...getInputProps({
                onClick: (e: any) => {
                  // allow selecting the SAME file again
                  e.target.value = "";
                },
              })}
            />

            {illustration && !hasFile && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={illustration}
                alt="File upload illustration"
                style={{ maxWidth: "100%" }}
              />
            )}

            {activeDeleteButton ? (
              hasFile ? (
                <StyledDefaultEmtIcon
                  className="emt-bin-1"
                  color="primary"
                  sx={{ cursor: "pointer" }}
                  onClick={handleRemove}
                />
              ) : (
                <StyledDefaultEmtIcon className="emt-add" color="primary" />
              )
            ) : !hasFile ? (
              <StyledDefaultEmtIcon className="emt-add" color="primary" />
            ) : null}
          </DefaultDropZoneBox>
        ) : (
          <SmallDropZoneBox
            isFocused={isFocused}
            isDragAccept={isDragAccept}
            isDragReject={isDragReject}
            file={fileBg}
            {...rootClickProps}
            {...(overrideOpenImageDialog && {
              onClick: () => overrideOpenImageDialog(open),
            })}
            sx={overrideDropZoneBox}
          >
            <input
              {...getInputProps({
                onClick: (e: any) => {
                  e.target.value = "";
                },
              })}
            />

            {!hasFile && <AddPictureIcon />}

            {hasFile && activeDeleteButton && (
              <StyledSmallEmtIcon
                className="emt-bin-1"
                color="primary"
                sx={{ position: "absolute", bottom: "-10px", right: "-10px" }}
                onClick={handleRemove}
              />
            )}
          </SmallDropZoneBox>
        )}
      </Box>

      {variant === "default" && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {fileRejections.length > maxFiles ? (
            <EmtTypography
              variant="caption"
              color="error"
              textAlign="center"
              sx={{ marginTop: "15px" }}
            >
              {errorTranslate["too-many-files"]}
            </EmtTypography>
          ) : fileRejections.length ? (
            fileRejections.map((rejection, key) => (
              <EmtTypography
                key={key}
                variant="caption"
                color="error"
                textAlign="center"
                sx={{ marginTop: "30px" }}
              >
                <u>{rejection.file.name}</u> :{" "}
                {rejection.errors.map((err) => `${errorTranslate[err.code]}`)}
                <br />
              </EmtTypography>
            ))
          ) : null}
        </Box>
      )}

      {helperText && (
        <StyledFormHelperText className={error ? "error" : ""}>
          {helperText}
        </StyledFormHelperText>
      )}
    </DropZoneBoxWrapper>
  );
}

export default EmtFileDropZone;
